import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import { FaEye, FaEdit, FaTrash } from 'react-icons/fa';

const ListMentors = () => {
    const navigate = useNavigate();
    const [mentors, setMentors] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMentors = async () => {
        try {
            setLoading(true);
            const res = await getRequest('mentors?all=true');
            setMentors(res?.data || []);
        } catch (err) {
            console.error('Failed to fetch mentors:', err);
            toast.error("Failed to load mentors list.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatusToggle = async (id, currentStatus) => {
        try {
            await patchRequest(`mentors/admin/${id}`, { is_active: !currentStatus });
            toast.success("Mentor status updated successfully!");
            fetchMentors(); // Refresh list
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this mentor and their user account?")) {
            try {
                await deleteRequest(`mentors/admin/${id}`);
                toast.success("Mentor deleted successfully!");
                fetchMentors();
            } catch (err) {
                console.error(err);
                toast.error("Failed to delete mentor.");
            }
        }
    };

    const tableHeaders = [
        {
            Header: 'ID',
            accessor: 'id',
        },
        {
            Header: 'Image',
            accessor: 'image',
            Cell: ({ row }) => (
                row.original.image ? (
                    <img
                        src={`${process.env.REACT_APP_BACKEND}uploads/${row.original.image}`}
                        alt="mentor"
                        width={50}
                        height={50}
                        style={{ objectFit: 'cover', borderRadius: '50%' }}
                    />
                ) : (
                    <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        backgroundColor: '#ccc',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '12px'
                    }}>
                        N/A
                    </div>
                )
            ),
        },
        {
            Header: 'Mentor Details',
            accessor: 'mentorName',
            Cell: ({ row }) => {
                const user = row.original.user || {};
                return (
                    <div className="text-left font-weight-bold">
                        {`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Name'}
                    </div>
                );
            }
        },
        {
            Header: 'Contact Info',
            accessor: 'contact',
            Cell: ({ row }) => {
                const user = row.original.user || {};
                return (
                    <div className="text-left" style={{ fontSize: '13px' }}>
                        <div><strong>Email:</strong> {user.email}</div>
                        <div><strong>Mobile:</strong> {user.mobile}</div>
                        <div><strong>Address:</strong> {user.address || 'N/A'}</div>
                    </div>
                );
            }
        },
        {
            Header: 'Expertise',
            accessor: 'expertise',
            Cell: ({ row }) => (
                <div className="text-left" style={{ fontSize: '13px' }}>
                    <div><strong>Qualification:</strong> {row.original.qualification || 'N/A'}</div>
                    <div><strong>Expertise:</strong> {row.original.expertise}</div>
                </div>
            )
        },
        {
            Header: 'Organization',
            accessor: 'organization',
            Cell: ({ row }) => (
                <div className="text-left" style={{ fontSize: '13px' }}>
                    <div><strong>Designation:</strong> {row.original.designation || 'N/A'}</div>
                    <div><strong>Company:</strong> {row.original.company || 'N/A'}</div>
                </div>
            )
        },
        {
            Header: 'Status',
            accessor: 'is_active',
            Cell: ({ row }) => (
                <button
                    className={`btn btn-sm ${row.original.is_active ? 'btn-success' : 'btn-secondary'}`}
                    onClick={() => handleStatusToggle(row.original.id, row.original.is_active)}
                    style={{ minWidth: '80px' }}
                >
                    {row.original.is_active ? 'Active' : 'Inactive'}
                </button>
            ),
        },
        {
            Header: 'Action',
            accessor: 'action',
            Cell: ({ row }) => (
                <div className="d-flex justify-content-center">
                    <button className="btn btn-sm btn-info mx-1" title="View" onClick={() => navigate(`/mentors/view/${row.original.id}`)}>
                        <FaEye />
                    </button>
                    <button className="btn btn-sm btn-primary mx-1" title="Edit" onClick={() => navigate(`/mentors/edit/${row.original.id}`)}>
                        <FaEdit />
                    </button>
                    <button className="btn btn-sm btn-danger mx-1" title="Delete" onClick={() => handleDelete(row.original.id)}>
                        <FaTrash />
                    </button>
                </div>
            )
        }
    ];

    useEffect(() => {
        fetchMentors();
    }, []);

    return (
        <>
            <div className="container-fluid mt-4">
                {/* Premium Animated Header Banner */}
                <div 
                    className="dashboard-header-card d-flex align-items-center justify-content-between mb-4 mx-2 shadow-lg" 
                    style={{ 
                        background: 'linear-gradient(135deg, #0d47a1 0%, #00695c 100%)', 
                        padding: '1.5rem 2rem', 
                        borderRadius: '15px',
                        animation: 'fadeInUp 0.6s ease-out forwards'
                    }}
                >
                    <div className="d-flex align-items-center">
                        <div className="icon-circle mr-3" style={{ background: 'rgba(255,255,255,0.2)', width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fa fa-users text-white" style={{ fontSize: '24px' }}></i>
                        </div>
                        <div>
                            <h3 className="text-white font-weight-bold mb-0" style={{ letterSpacing: '0.5px' }}>Mentors Directory</h3>
                            <p className="text-white-50 mb-0 small">Manage your expert mentors</p>
                        </div>
                    </div>
                    <button 
                        className="btn btn-light font-weight-bold shadow-sm" 
                        onClick={() => navigate('/mentors/add')}
                        style={{ color: '#0d47a1', borderRadius: '8px', padding: '10px 20px', transition: 'all 0.3s' }}
                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }} onFocus={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(255,255,255,0.3)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }} onBlur={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                        <i className="fa fa-plus mr-2"></i>
                        Add New Mentor
                    </button>
                </div>
                {loading ? (
                    <div className="text-center my-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Loading...</span>
                        </div>
                    </div>
                ) : mentors.length > 0 ? (
                    <AdminTable tableHeaders={tableHeaders} tableData={mentors} />
                ) : (
                    <div className="text-center my-5">
                        <p>No mentors registered yet.</p>
                    </div>
                )}
            </div>
        </>
    );
};

export default ListMentors;
